import React, { useEffect, useState } from 'react';
import {
  ThemeProvider, Box, Typography, Divider, Dialog, DialogActions, IconButton,
  DialogContent, DialogTitle, Tooltip
} from '@mui/material';
import { theme } from '../../Medical_History_Forms/themeprovider';
import Grid from '@mui/material/Grid2';
import {
  convertUnderscoreToTitle, infoTextStyle, sectionTitleStyle
} from '../../../utils/commonUtils';
import { getLaparoscopy, LaparoscopyType } from './laparoscopy.helper';
import { useLoader } from '../../loaderProvider/loader';
import styles from '../oocyteRetrieval/oocyteRetrival.module.css';
import { Close as CloseIcon } from '@mui/icons-material';
import ImageLightbox from '../ImageLightbox';

interface LaparoscopyViewDetailsProps {
  record: number;
  onBack: () => void;
  onEdit?: () => void;
}

const LaparoscopyViewDetails: React.FC<LaparoscopyViewDetailsProps> = ({ record, onBack, onEdit }) => {
  const { showLoader, hideLoader } = useLoader();
  const [retrievalData, setRetrievalData] = useState<LaparoscopyType | null>(null);
  const [open, setOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState('');

  useEffect(() => {
    if (!record) return;

    showLoader();
    getLaparoscopy(record)
      .then((data) => setRetrievalData(data))
      .catch(() => setRetrievalData(null))
      .finally(() => hideLoader());
  }, [record, showLoader, hideLoader]);

  const handleImageClick = (src: string) => {
    setImageSrc(src);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  const handleOpenDialog = (index: number) => {
    if (!retrievalData?.images) return;
    const urls = retrievalData.images.map(img => img.image);
    setSelectedImages(urls);
    setSelectedImageIndex(index);
    setOpenImageDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenImageDialog(false);
    setSelectedImages([]);
    setSelectedImageIndex(0);
  };
  
  const renderSection = (label: string, value: string | number | null  | undefined) => {
    if (!value) return null;
    return (
      <Grid size={3} sx={{ textAlign: 'left' }}>
        <Typography variant="body1" sx={sectionTitleStyle}>{label}</Typography>
        <Typography variant="body2" sx={infoTextStyle}>{value}</Typography>
      </Grid>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3 }}>
        <Grid container spacing={4} mb={3}>
          {renderSection('Date', retrievalData?.date)}
          {renderSection('Indication', retrievalData?.indication)}
          {renderSection('Cycle Day', `${retrievalData?.cycle_day || '-'} Day`)}
          {renderSection('Procedure Duration', `${retrievalData?.procedure_duration || '-'} (Hrs)`)}
        </Grid>

        <Divider sx={{ borderColor: '#666', my: 2 }} />

        {/* Anesthesia Section */}
        {(retrievalData?.anesthesia_type || retrievalData?.anesthesia_details_remark || retrievalData?.preanesthetic_checkup) && (
          <>
            <Box sx={{ width: '100%' }} mt={4}>
              <Typography variant="h6" sx={infoTextStyle}>Anesthesia Details</Typography>
            </Box>
            <Grid container spacing={4} mt={2} mb={3}>
              {renderSection('Anesthesia Type', convertUnderscoreToTitle(retrievalData?.anesthesia_type))}
              {renderSection('Anesthesia Details', retrievalData?.anesthesia_details_remark)}
              {renderSection('Preanesthetic Check Up', retrievalData?.preanesthetic_checkup)}
            </Grid>
            <Divider sx={{ borderColor: '#666', my: 2 }} />
          </>
        )}

        {/* Patency Of Fallopian Tube */}
        {(retrievalData?.right_tube_status || retrievalData?.left_tube_status) && (
          <>
            <Box sx={{ width: '100%' }} mt={4}>
              <Typography variant="h6" sx={infoTextStyle}>Patency Of Fallopian Tube</Typography>
            </Box>
            <Grid container spacing={4} mt={1} mb={3}>
              {renderSection('Right Tube', retrievalData?.right_tube_status)}
              {renderSection('Left Tube', retrievalData?.left_tube_status)}
            </Grid>
            <Divider sx={{ borderColor: '#666', my: 2 }} />
          </>
        )}

        {/* Right Ovary */}
        {(retrievalData?.right_ovary_status || retrievalData?.right_ovary_details) && (
          <>
            <Box sx={{ width: '100%' }} mt={4}>
              <Typography variant="h6" sx={infoTextStyle}>Right Ovary</Typography>
            </Box>
            <Grid container spacing={4} mt={1} mb={3}>
              {renderSection('Status', retrievalData?.right_ovary_status)}
              {renderSection('Other Details', retrievalData?.right_ovary_details)}
            </Grid>
            <Divider sx={{ borderColor: '#666', my: 2 }} />
          </>
        )}

        {/* Left Ovary */}
        {(retrievalData?.left_ovary_status || retrievalData?.left_ovary_details) && (
          <>
            <Box sx={{ width: '100%' }} mt={4}>
              <Typography variant="h6" sx={infoTextStyle}>Left Ovary</Typography>
            </Box>
            <Grid container spacing={4} mt={1} mb={3}>
              {renderSection('Status', retrievalData?.left_ovary_status)}
              {renderSection('Other Details', retrievalData?.left_ovary_details)}
            </Grid>
            <Divider sx={{ borderColor: '#666', my: 2 }} />
          </>
        )}

        {/* Other Details */}
        {(retrievalData?.adhesion_site || retrievalData?.adhesion_grade || retrievalData?.remark ||
          retrievalData?.endometriosis_grade || retrievalData?.uterus_status || retrievalData?.uterus_size ||
          retrievalData?.uterus_shape || retrievalData?.uterus_position || retrievalData?.uterus_mobility ||
          retrievalData?.associated_procedures || retrievalData?.eb_hpe_remark || retrievalData?.done_by_name ||
          retrievalData?.associated_doctor_name) && (
          <>
            <Box sx={{ width: '100%' }} mt={4}>
              <Typography variant="h6" sx={infoTextStyle}>Other Details</Typography>
            </Box>
            <Grid container spacing={4} mt={1} mb={3}>
              {renderSection('Site', convertUnderscoreToTitle(retrievalData?.adhesion_site))}
              {renderSection('Adhesion', convertUnderscoreToTitle(retrievalData?.adhesion_grade))}
              {renderSection('Remark', retrievalData?.remark)}
              {renderSection('Endometriosis Grade', convertUnderscoreToTitle(retrievalData?.endometriosis_grade))}
              {renderSection('Uterus', convertUnderscoreToTitle(retrievalData?.uterus_status))}
              {renderSection('Size', convertUnderscoreToTitle(retrievalData?.uterus_size))}
              {renderSection('Shape', convertUnderscoreToTitle(retrievalData?.uterus_shape))}
              {renderSection('Position', convertUnderscoreToTitle(retrievalData?.uterus_position))}
              {renderSection('Mobility', convertUnderscoreToTitle(retrievalData?.uterus_mobility))}
              {renderSection('Associated Procedures', convertUnderscoreToTitle(retrievalData?.associated_procedures))}
              {renderSection('Remark', retrievalData?.eb_hpe_remark)}
              {renderSection('Done By', retrievalData?.done_by_name)}
              {renderSection('Associated Dr.', retrievalData?.associated_doctor_name)}
            </Grid>
            <Divider sx={{ borderColor: '#666', my: 2 }} />
          </>
        )}


        {/* Image Gallery */}
        {retrievalData?.images && retrievalData.images.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="body1" sx={sectionTitleStyle}>
              Images
            </Typography>
            <Grid container spacing={2}>
              {retrievalData.images.map((img, idx) => (
                <Grid key={img.id}>
                  <img
                    src={img.image}
                    alt={`Laporoscopy ${idx + 1}`}
                    style={{ width: '40px', height: '40px', cursor: 'pointer', borderRadius: 25 }}
                    onClick={() => handleOpenDialog(idx)}
                  />
                </Grid>
              ))}

            </Grid>
          </Box>
        )}


        <ImageLightbox
          open={openImageDialog}
          images={selectedImages}
          initialIndex={selectedImageIndex}  // Pass the index here
          onClose={handleCloseDialog}
          label="Preview"
        />

        {/* Image Preview */} 
        <Tooltip title="Click to preview" arrow>
          <div
            onClick={() => handleImageClick(retrievalData?.image_path_url || '')}
            style={{ cursor: 'pointer', position: 'relative' }}
          >
            {retrievalData?.image_path_url && (
              <img
                src={retrievalData?.image_path_url}
                alt="file"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                }}
                onMouseOver={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.transform = 'scale(1.1)';
                  target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                }}
                onMouseOut={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.transform = 'scale(1)';
                  target.style.boxShadow = 'none';
                }}
              />
            )}
          </div>
        </Tooltip>

        <Dialog open={open} onClose={handleClose} maxWidth="md" sx={{ width: '55%', margin: 'auto' }}>
          <DialogTitle sx={{ position: 'relative' }}>
            Image Preview
            <IconButton onClick={handleClose} color="primary" sx={{ position: 'absolute', top: 10, right: 4, padding: '8px' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ padding: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <img
              src={imageSrc}
              alt="Large Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          </DialogContent>
          <DialogActions>
            <IconButton onClick={handleClose} color="primary">Close</IconButton>
          </DialogActions>
        </Dialog>

        <Box className={styles.customButtonDiv}>
          <button type="button" className={styles.resetButton} onClick={onBack}>Back to List</button>
          <button type="button" className={styles.customButton} onClick={onEdit}>
            <span style={{ fontWeight: 'bold' }}>Edit</span>
          </button>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default LaparoscopyViewDetails; 
